import React, {useState, useEffect} from 'react'
import * as PropTypes from 'prop-types'
import {InferPropTypes} from '@modifi/typescript-utils'
import moment from 'moment'
import {Col, Row, SkeletonLine} from '@modifi/ui'
import {FormattedDate} from '@modifi/plugin-i18n'

import ShipLine from '../ship-line'
import {Wrapper, ProgressBarLabel} from './styles'

const propTypes = {
  shipment: PropTypes.shape({
    shippingStartDate: PropTypes.string,
    estimatedShippingStartDate: PropTypes.string,
    shippingEndDate: PropTypes.string,
    estimatedShippingEndDate: PropTypes.string,
    origin: PropTypes.shape({
      countryCode: PropTypes.string,
      name: PropTypes.string,
    }),
    destination: PropTypes.shape({
      countryCode: PropTypes.string,
      name: PropTypes.string,
    }),
  }),
  isLoading: PropTypes.bool,
}
const defaultProps = {
  shipment: undefined,
  isLoading: false,
}

type ProgressBarProps = InferPropTypes<typeof propTypes, typeof defaultProps>

const ProgressBar: React.FC<ProgressBarProps> = ({isLoading, shipment}) => {
  const startDate = shipment?.shippingStartDate ?? shipment?.estimatedShippingStartDate
  const endDate = shipment?.shippingEndDate ?? shipment?.estimatedShippingEndDate
  const [startingPort, setStartingPort] = useState('')
  const [destinationPort, setDestinationPort] = useState('')

  useEffect(() => {
    if (shipment?.origin?.name && shipment?.origin?.countryCode) {
      setStartingPort(shipment?.origin?.name.concat(',', shipment?.origin?.countryCode))
    } else {
      setStartingPort('')
    }

    if (shipment?.destination?.name && shipment?.destination?.countryCode) {
      setDestinationPort(
        shipment?.destination?.name.concat(',', shipment?.destination?.countryCode)
      )
    } else {
      setStartingPort('')
    }
  }, [shipment])

  const progressPercent =
    startDate && endDate
      ? Math.min(
          (moment().diff(startDate, 'seconds') / moment(endDate).diff(startDate, 'seconds')) * 100,
          100
        )
      : 0

  return (
    <Wrapper>
      <Row justifyContent="between" mb={10}>
        <Col auto>
          <ProgressBarLabel
            data-ident={isLoading ? 'shipment-origin-loading' : 'shipment-origin-loaded'}
            $bold
            $departure
          >
            {isLoading ? <SkeletonLine stringLength={25} /> : <span>{startingPort}</span>}
          </ProgressBarLabel>
          <ProgressBarLabel $departure>
            {isLoading ? <SkeletonLine /> : <FormattedDate>{startDate}</FormattedDate>}
          </ProgressBarLabel>
        </Col>
        <Col auto>
          <ProgressBarLabel $bold>
            {isLoading ? <SkeletonLine stringLength={25} /> : <span>{destinationPort}</span>}
          </ProgressBarLabel>
          <ProgressBarLabel>
            {isLoading ? <SkeletonLine /> : <FormattedDate>{endDate}</FormattedDate>}
          </ProgressBarLabel>
        </Col>
      </Row>
      <ShipLine progress={progressPercent} isLoading={!!isLoading} />
    </Wrapper>
  )
}

ProgressBar.propTypes = propTypes
ProgressBar.defaultProps = defaultProps

export default ProgressBar
